
import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useBlogPost } from '../hooks/useBlogPosts';
import '../components/PromoBox.css';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { post, loading } = useBlogPost(slug);

  useEffect(() => {
    if (!loading && !post) {
      navigate('/blog');
    }
    window.scrollTo(0, 0);
  }, [post, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
          <p className="mt-4 text-gray-500">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) return null;

  // Generate structured data for the blog post (JSON-LD)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription || post.excerpt,
    "image": post.image,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Vaulto",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vaulto.social/vaulto.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://vaulto.social/blog/${post.slug}`
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{post.title} | Vaulto Blog</title>
        <meta name="title" content={`${post.title} | Vaulto Blog`} />
        <meta name="description" content={post.metaDescription || post.excerpt} />
        <meta name="author" content={post.author} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://vaulto.social/blog/${post.slug}`} />
        
        {/* Keywords */}
        {post.keywords && <meta name="keywords" content={post.keywords} />}
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://vaulto.social/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription || post.excerpt} />
        <meta property="og:image" content={post.image} />
        <meta property="og:site_name" content="Vaulto" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://vaulto.social/blog/${post.slug}`} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.metaDescription || post.excerpt} />
        <meta name="twitter:image" content={post.image} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <Navbar />
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
      <article className="max-w-3xl mx-auto bg-white rounded-3xl overflow-hidden border border-gray-200 shadow-xl">
        <div className="relative h-64 sm:h-96 w-full">
          <img 
            src={post.image} 
            alt={post.title} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent opacity-60"></div>
        </div>

        <div className="px-6 sm:px-10 py-8 sm:py-12 -mt-20 relative bg-white">
          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-4 text-sm text-pink-600 font-medium mb-4">
              <span className="bg-pink-50 px-3 py-1 rounded-full border border-pink-200">
                {new Date(post.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
              <span className="flex items-center text-gray-500">
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {post.readTime}
              </span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6">
              {post.title}
            </h1>
            
            <div className="flex items-center border-b border-gray-200 pb-8">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 flex items-center justify-center text-white font-bold text-lg">
                {post.author.charAt(0)}
              </div>
              <div className="ml-4">
                <p className="text-gray-900 font-medium">{post.author}</p>
                <p className="text-gray-500 text-sm">Contributor</p>
              </div>
            </div>
          </div>

          <div className="prose prose-lg max-w-none
            prose-headings:text-gray-900 prose-headings:font-bold 
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:border-l-4 prose-h2:border-pink-500 prose-h2:pl-4
            prose-h3:text-xl prose-h3:text-pink-600 prose-h3:mt-8
            prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
            prose-a:text-pink-600 prose-a:no-underline hover:prose-a:text-pink-700 hover:prose-a:underline
            prose-ul:list-disc prose-ul:pl-5 prose-ul:space-y-2 prose-ul:text-gray-700 prose-ul:mb-6
            prose-ol:list-decimal prose-ol:pl-5 prose-ol:space-y-2 prose-ol:text-gray-700 prose-ol:mb-6
            prose-li:marker:text-pink-500
            prose-strong:text-gray-900 prose-strong:font-semibold
            prose-img:rounded-lg prose-img:shadow-lg prose-img:my-8
            prose-figure:my-8
            prose-figcaption:text-center prose-figcaption:text-gray-500 prose-figcaption:text-sm prose-figcaption:mt-2
            bg-transparent
            ">
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>

          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="bg-gradient-to-r from-pink-50 to-violet-50 rounded-2xl p-8 border border-pink-200 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Never Lose Your Instagram Memories</h3>
              <p className="text-gray-600 mb-6 max-w-xl mx-auto">
                Join thousands of creators who trust Vaulto to securely backup their Instagram posts, stories, and reels to Google Drive.
              </p>
              <Link 
                to="/register" 
                className="inline-block bg-gradient-to-r from-pink-600 to-violet-600 hover:from-pink-700 hover:to-violet-700 text-white font-bold py-3 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-pink-500/25 transform hover:-translate-y-1"
              >
                Start Free Backup
              </Link>
            </div>
          </div>

          <div className="mt-12 flex justify-center">
            <Link 
              to="/blog"
              className="flex items-center text-gray-500 hover:text-gray-900 transition-colors font-medium"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Blog
            </Link>
          </div>
        </div>
      </article>
      </div>
      <Footer />
    </div>
  );
};

export default BlogPost;
